import React, { useMemo, useState } from "react";
import { colors2, getBreakPoint, useScreenWidth } from "oolib";
import { SIGNAL_CATEGORY_FILL } from "@/components/analysisModules/comps/SignalFilters/signalCategories";
import TableElem from "@/components/cards/TableElem";
import { PillSwitch } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/PillSwitch";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { NO_ZONE, resolveZones, resolveWinCopy } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/zoneStyles";
import { ZONE_ORDER } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/CONFIG";
import { keyLabel } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/periodKeys";
import { grainNoun } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";
import { momentumText, severityText, toneColor, winTone, zoneKeyOf } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";

/**
 * The ranked list under the field, on TableElem. Two tabs: Priorities
 * (risk-bearing sub-topics, in the scorer's order) and Wins (satisfaction
 * sub-topics, by momentum). A row click opens the shared sub-topic panel.
 * At the Topics grain (`scoreGrain` "buckets") the rows are whole topics:
 * the first column is headed "Topic" and the parent-topic column goes.
 *
 * Its columns live in one list (COLUMN_SPECS) that both the table and the
 * column picker read, so a report widget can hide any of them and adding a
 * column takes one entry.
 *
 * One column per reading, each on a table cell block: sub-topic, zone
 * pill, signal (grey pill, dot in its category's accent), topic, one count per period
 * in the trend window (the selected period emphasised), the share those
 * counts are of their own period and the gap between the two in percentage
 * points, the shape of that
 * series, momentum vs the window's first period (arrowed percent, or a
 * "New" pill),
 * severity as a fill bar, and the dataset circles. Every column sorts in
 * the table's own front-end sort; it opens sorted by zone (fires first,
 * the scorer's rank), which keeps the scorer's order within a zone. The
 * signal column ranks the same way: strongest signal first (its severity
 * weight, the one the scorer used), not A–Z.
 */

// The click payload: the scored item's node MINUS its observations.
//
// Topics V2 never fetches annotations — the counts endpoint sends aggregates
// (a sub-topic node is ~400 bytes) and usePrioritiesScoring unrolls them into
// one PSEUDO observation per annotation so V1's array-walking scorer can run
// unchanged. That list is an input to the scoring, never a reading: the side
// panel this payload opens reads nodeId / label / signalId / category /
// parkedOf / isUnplaced and fetches its own annotations. Carrying it here
// would put the whole unrolled tree inside the table's data — 14 MB and 64k
// objects on ShikshaLokam staging, which TableElem then serialises on every
// render (JSON.stringify in its sort deps) and deep-clones on every sort.
// Everything else on the node travels, so a consumer reading some other
// field still finds it.
const clickPayloadOf = ({ observations, ...node }) => node;

const pct = (n) => `${n.toFixed(1)}%`;
// Percentage points carry their sign explicitly — "+3.9", "−1.2", "0.0" —
// with the true minus, as format.js does for every other signed reading.
const signedPts = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(1)}`;

// The two shares the momentum is built from. Delta mode compares the
// selected period with the window's first; history mode compares it with the
// sub-topic's own normal, so "from" is that baseline rather than a named
// period. Both are shares OF THEIR OWN PERIOD, which is why a sub-topic can
// grow in count and still fall here: the corpus grew faster.
const sharesOf = (momentum) => {
  if (!momentum?.available) return { from: null, to: null };
  const from = momentum.mode === "history" ? momentum.baseline : momentum.prev?.share;
  const to = momentum.shareNow;
  if (typeof from !== "number" || typeof to !== "number") return { from: null, to: null };
  return { from, to };
};

// The Change cell. Delta mode: the count in the selected period against the
// count in the comparison period, as a percent, its arrow and colour following
// the direction (inverted on the wins tab — rising praise is good). From zero
// there is no percent to take: "New" when something arrived, "no change" when
// nothing did. History mode: the scorer's momentum wording, unchanged.
const changeOf = ({ item, m, tone, isWins }) => {
  const mo = item.momentum;
  if (!mo?.available || mo.mode !== "delta") {
    return {
      momentumLabel: m.text,
      momentumHint: m.reason || "",
      momentumArrow: m.tone === "up" ? "up" : m.tone === "down" ? "down" : null,
      momentumSemibold: m.tone === "up" || m.tone === "down",
      momentumTextColor: m.tone === "up" || m.tone === "down" ? toneColor(tone) : colors2.grey80,
      momentumBadge: mo?.band === "new" ? "New" : null,
      momentumDelta: mo?.available ? mo.momentum || 0 : 0,
    };
  }
  const from = mo.prev?.n || 0;
  const to = mo.nNow || 0;
  if (from === 0) {
    return {
      momentumLabel: to > 0 ? "new" : "no change",
      momentumHint: to > 0 ? `None in the comparison period, ${to} now` : "",
      momentumArrow: null,
      momentumSemibold: false,
      momentumTextColor: colors2.grey80,
      momentumBadge: to > 0 ? "New" : null,
      // Anything arriving from nothing sorts above every measured rise.
      momentumDelta: to > 0 ? 1e9 : 0,
    };
  }
  const pct = Math.round(((to - from) / from) * 100);
  const direction = pct > 0 ? "up" : pct < 0 ? "down" : "flat";
  const shown = isWins ? winTone(direction) : direction;
  return {
    momentumLabel: pct === 0 ? "no change" : `${Math.abs(pct)}%`,
    momentumHint: `${from} → ${to}`,
    momentumArrow: direction === "flat" ? null : direction,
    momentumSemibold: direction !== "flat",
    momentumTextColor: direction === "flat" ? colors2.grey80 : toneColor(shown),
    momentumBadge: null,
    momentumDelta: pct,
  };
};

// The scored item, flattened to what the cells and the sorts read.
const rowOf = ({ item, meta, isWins, zones, win, categoryTheme }) => {
  // The header names the comparison period; the cells carry just the change.
  const m = momentumText(item, { bare: true });
  const tone = isWins ? winTone(m.tone) : m.tone;
  const zone = item.isWin ? null : item.zone ? zones[item.zone] : NO_ZONE;
  const periodCounts = {};
  for (const k of meta.windowKeys || []) periodCounts[k] = item.periodCounts?.[k] || 0;
  const { from: shareFrom, to: shareTo } = sharesOf(item.momentum);
  const hasShares = shareFrom !== null;
  const sharePts = hasShares ? shareTo - shareFrom : 0;
  const fromLabel = item.momentum?.mode === "history" ? "a normal period" : meta.prevLabel || "the earlier period";
  return {
    nodeId: item.nodeId,
    label: item.label,
    bucketLabel: item.bucketLabel || "",
    zoneKey: zoneKeyOf(item),
    zoneLabel: zone ? zone.label : win.label,
    zoneBg: zone ? zone.fill : categoryTheme.fillOf(item.category),
    zoneColor: zone ? zone.onFill : categoryTheme.styleOf(item.category).textOnFill,
    zoneHint: zone?.hint || "",
    signalDisplay: item.signal?.display || "",
    signalAccent: item.signal?.category ? categoryTheme.accentOf(item.signal.category) : colors2.grey60,
    signalCategory: item.signal?.category || null,
    // The signal's strength — its own severity weight, else its category's,
    // else the default; whichever the scorer multiplied in.
    signalWeight: item.severity.weight,
    periodCounts,
    // CHANGE (delta mode) is the change in COUNT between the two periods the
    // columns beside it show — Dec 183 → Jul 396 reads +116%. The share's
    // movement already has its own columns (Share, Pts), so this one does not
    // repeat it; the relative SHARE change it used to show (88% on that row)
    // read as a wrong count change. History mode keeps momentum in swings.
    ...changeOf({ item, m, tone, isWins }),
    // The two shares the change is computed from, and the gap between them
    // in percentage points. The label fields go null when there is nothing
    // to show, so those cells fall back to "–"; the numeric ones stay
    // numbers, so the sorts never meet a null.
    shareFrom: hasShares ? shareFrom : null,
    shareTo: hasShares ? shareTo : null,
    shareNowValue: hasShares ? shareTo : 0,
    shareShift: hasShares ? `${pct(shareFrom)} → ${pct(shareTo)}` : null,
    sharePtsValue: sharePts,
    // TableChange counts as always populated, so it needs the dash itself;
    // the Share column beside it gets one from the populated check.
    sharePtsLabel: hasShares ? `${signedPts(sharePts)} pts` : "–",
    // Only a gap worth reading wears the tone; a hair either way stays plain.
    sharePtsColor: hasShares && Math.abs(sharePts) >= 0.05 ? toneColor(tone) : colors2.grey80,
    shareHint: hasShares
      ? `${pct(shareFrom)} of everything said in ${fromLabel}, ${pct(shareTo)} in ${meta.nowLabel} — a gap of ${signedPts(sharePts)} percentage points`
      : "",
    shapeColor: m.tone === "up" || m.tone === "down" ? toneColor(tone) : colors2.grey40,
    severityValue: item.severity.value,
    severityDisplay: severityText(item),
    severityColor: item.category ? categoryTheme.accentOf(item.category) : colors2.grey60,
    datasets: item.datasets,
    datasetCount: item.datasets.length,
    row: clickPayloadOf(item.row),
  };
};

const PERIOD_COL_WIDTH = 76;

// The signal column's rank, strongest first — the counterpart of ZONE_ORDER
// for the zone column. Weight decides; ties fall back to the tenant's
// category order, then the name, so the order is stable across renders.
const signalOrderOf = (rows, categoryTheme) => {
  const byDisplay = new Map();
  for (const r of rows) {
    if (!r.signalDisplay || byDisplay.has(r.signalDisplay)) continue;
    byDisplay.set(r.signalDisplay, r);
  }
  const catRank = (r) => (r.signalCategory && categoryTheme.order[r.signalCategory]) ?? Infinity;
  return [...byDisplay.values()]
    .sort((a, b) => b.signalWeight - a.signalWeight || catRank(a) - catRank(b) || a.signalDisplay.localeCompare(b.signalDisplay))
    .map((r) => r.signalDisplay);
};

/**
 * The table's columns, in order — ONE list, read by two things: the table
 * itself, and the column picker a report widget offers its author. Adding a
 * column here puts it in both, with nothing else to keep in sync, and it
 * shows by default, because config stores what is HIDDEN rather than what is
 * shown (a column added after a widget was saved would otherwise be missing
 * from its list, and so silently stay off).
 *
 *   colId     what config stores. Don't rename a shipped one — a saved
 *             widget's hidden list names it.
 *   display   the picker's label. `build` may return a livelier
 *             headerDisplay (the momentum column names its comparison).
 *   width     folded into colWidths; a group spec gives it to each of its
 *             columns.
 *   required  cannot be hidden, and so is not offered in the picker.
 *   hiddenWhen (ctx) => true drops the column for this reading whatever the
 *             author picked (the parent-topic column at the Topics grain).
 *   build     (ctx) => one column config, or an array of them.
 */
const COLUMN_SPECS = [
  {
    colId: "label",
    display: "Sub-topic",
    width: 280,
    // The row's name: with it hidden there is nothing to read a row by.
    required: true,
    // Headed by what the rows ARE: sub-topics, or whole topics when the
    // layout scores at the Topics grain.
    build: ({ isBuckets }) => ({
      colId: "label",
      headerDisplay: isBuckets ? "Topic" : "Sub-topic",
      comp: "TableText",
      valuePath: "label",
      props: { typoComp: "UI_BODY_SEMIBOLD_SM", color: colors2.grey80 },
      sortConfig: { fn: "alphabetical", path: "label" },
    }),
  },
  {
    colId: "zone",
    display: "Zone",
    width: 116,
    build: () => ({
      colId: "zone",
      headerDisplay: "Zone",
      comp: "TablePill",
      valuePath: "zoneLabel",
      dynamicPropsCallback: ({ tableRowData }) => ({ bg: tableRowData.zoneBg, color: tableRowData.zoneColor, hint: tableRowData.zoneHint }),
      // Zones rank, they don't alphabetise: the scorer's order, fires
      // first. Opens on it (the sort is stable, so within a zone the
      // scorer's severity-then-momentum order holds). With this column
      // hidden nothing carries defaultSort, and the rows render in the
      // order the scorer handed over — the same reading.
      sortConfig: { path: "zoneKey", order: [...ZONE_ORDER, "none", "win"] },
      defaultSort: true,
      defaultSortBy: "b",
    }),
  },
  {
    colId: "signal",
    display: "Signal",
    width: 156,
    build: ({ signalOrder }) => ({
      colId: "signal",
      headerDisplay: "Signal",
      comp: "TablePill",
      valuePath: "signalDisplay",
      dynamicPropsCallback: ({ tableRowData }) => ({ dot: tableRowData.signalAccent }),
      // Signals rank like zones: strongest first on the same click that
      // puts fires first. No signal goes last.
      sortConfig: { path: "signalDisplay", order: signalOrder },
    }),
  },
  {
    colId: "topic",
    display: "Topic",
    width: 180,
    // The row's parent topic. A topic row has none, so at the Topics grain
    // this column is dropped whatever the author's column picks say.
    hiddenWhen: ({ isBuckets }) => isBuckets,
    build: () => ({
      colId: "topic",
      headerDisplay: "Topic",
      comp: "TableText",
      valuePath: "bucketLabel",
      props: { typoComp: "UI_CAPTION", color: colors2.grey50 },
      sortConfig: { fn: "alphabetical", path: "bucketLabel" },
    }),
  },
  {
    // One column per period in the trend window, so the ids depend on the
    // window — they are one thing to show or hide, under a stable id.
    colId: "periods",
    display: "Count per period",
    width: PERIOD_COL_WIDTH,
    build: ({ meta, keys }) =>
      keys.map((k, i) => ({
        colId: `period_${k}`,
        headerDisplay: keyLabel(k, meta.timeGrain),
        comp: "TableNumber",
        valuePath: `periodCounts.${k}`,
        props: { emphasis: i === keys.length - 1 },
        dynamicPropsCallback: ({ tableRowData }) => {
          const n = tableRowData.periodCounts[k] || 0;
          const total = meta.totalsByKey?.[k];
          return { hint: total ? `${((n / total) * 100).toFixed(1)}% of the ${total} observations in ${keyLabel(k, meta.timeGrain)}` : undefined };
        },
        sortConfig: { fn: "numerical", path: `periodCounts.${k}` },
      })),
  },
  {
    // The counts above as shares of their OWN period — the numbers the
    // momentum is actually computed from, and the reason a row can gain
    // observations and still read as cooling.
    colId: "share",
    display: "Share of period",
    width: 140,
    build: () => ({
      colId: "share",
      headerDisplay: "Share",
      comp: "TableShareShift",
      valuePath: "shareShift",
      dynamicPropsCallback: ({ tableRowData }) => ({
        from: tableRowData.shareFrom,
        to: tableRowData.shareTo,
        hint: tableRowData.shareHint,
      }),
      sortConfig: { fn: "numerical", path: "shareNowValue" },
    }),
  },
  {
    // The gap between those two shares, in percentage points — the plain
    // arithmetic behind the momentum column's relative read.
    colId: "sharePts",
    display: "Share change (pts)",
    width: 92,
    build: () => ({
      colId: "sharePts",
      headerDisplay: "Pts",
      // A change, so it wears TableChange — but without the arrow, which the
      // momentum column already carries for the same movement.
      comp: "TableChange",
      valuePath: "sharePtsLabel",
      dynamicPropsCallback: ({ tableRowData }) => ({
        color: tableRowData.sharePtsColor,
        hint: tableRowData.shareHint,
      }),
      sortConfig: { fn: "numerical", path: "sharePtsValue" },
    }),
  },
  {
    colId: "shape",
    display: "Trend",
    width: 120,
    build: ({ meta, keys }) => ({
      colId: "shape",
      headerDisplay: "Trend",
      comp: "TableSparkline",
      valuePath: "periodCounts",
      props: { keys, interval: meta.timeGrain },
      dynamicPropsCallback: ({ tableRowData }) => ({ color: tableRowData.shapeColor, label: tableRowData.label }),
    }),
  },
  {
    // The read with its arrow — a percent vs the comparison period, or
    // swings from normal in history mode — and a "New" pill when there
    // is no earlier share to change from.
    colId: "momentum",
    display: "Change / momentum",
    width: 190,
    build: ({ meta }) => ({
      colId: "momentum",
      // Delta mode is a change against one named period; history mode is
      // momentum against the sub-topic's normal over the earlier periods.
      headerDisplay:
        meta.mode === "history"
          ? `Momentum (over ${meta.historyPeriods} ${grainNoun(meta.timeGrain)})`
          : meta.prevLabel
          ? `Change (vs ${meta.prevLabel})`
          : "Change",
      comp: "TableChange",
      valuePath: "momentumLabel",
      dynamicPropsCallback: ({ tableRowData }) => ({
        arrow: tableRowData.momentumArrow,
        color: tableRowData.momentumTextColor,
        semibold: tableRowData.momentumSemibold,
        badge: tableRowData.momentumBadge,
        badgeBg: SIGNAL_CATEGORY_FILL.userBehaviour,
        badgeColor: colors2.grey80,
        hint: tableRowData.momentumHint,
      }),
      sortConfig: { fn: "numerical", path: "momentumDelta" },
    }),
  },
  {
    colId: "severity",
    display: "Severity",
    width: 130,
    build: ({ maxSeverity }) => ({
      colId: "severity",
      headerDisplay: "Severity",
      comp: "TableCountBar",
      valuePath: "severityValue",
      props: { maxCount: maxSeverity, compact: true },
      dynamicPropsCallback: ({ tableRowData }) => ({ color: tableRowData.severityColor, display: tableRowData.severityDisplay }),
      sortConfig: { fn: "numerical", path: "severityValue" },
    }),
  },
  {
    colId: "sources",
    display: "Sources",
    width: 110,
    build: ({ titleByContentType }) => ({
      colId: "sources",
      headerDisplay: "Sources",
      comp: "TableDatasetCircles",
      valuePath: "datasets",
      props: { titleByContentType },
      sortConfig: { fn: "numerical", path: "datasetCount" },
    }),
  },
];

/**
 * The columns an author may hide, in the table's own order — derived from
 * COLUMN_SPECS, so a new column needs no second list. Feeds the Priorities
 * widget's column picker.
 */
export const PRIORITIES_TABLE_COLUMN_OPTIONS = COLUMN_SPECS.filter((spec) => !spec.required).map(
  ({ colId, display }) => ({ value: colId, display })
);

// A stable empty default, so "nothing hidden" doesn't hand the memo below a
// fresh array on every render.
const NO_COLUMNS_HIDDEN = [];

/**
 * Build every column, and name the hidden ones in `hiddenColIds` — TableElem's
 * own way of dropping a column, which it applies AFTER its sort hook has read
 * the full cellData.
 *
 * That ordering is the reason to hide this way rather than leave the column out
 * of cellData: the table's active sort is held by colId, and its
 * handleSortTableData looks that id up in cellData with no fallback. A column
 * missing from cellData while the sort still named it threw on the next
 * render — and the Zone column carries the default sort, so it was the first
 * thing an author would hide. Keeping it in cellData also keeps its sort
 * working: hide Zone and the rows stay in the scorer's order.
 */
const tableConfigOf = ({ meta, maxSeverity, titleByContentType, signalOrder, hiddenColumns = NO_COLUMNS_HIDDEN, isMobile = false, isBuckets = false }) => {
  const keys = meta.windowKeys || [];
  const ctx = { meta, keys, maxSeverity, titleByContentType, signalOrder, isBuckets };
  const hidden = new Set(hiddenColumns);

  const cellData = [];
  const colWidths = {};
  const hiddenColIds = [];
  for (const spec of COLUMN_SPECS) {
    // Hidden by the author, or moot for what the rows are (spec.hiddenWhen).
    const specHidden = !spec.required && (hidden.has(spec.colId) || !!spec.hiddenWhen?.(ctx));
    const built = spec.build(ctx);
    for (const col of Array.isArray(built) ? built : [built]) {
      cellData.push(col);
      if (spec.width) colWidths[col.colId] = spec.width;
      // A group spec (the per-period counts) hides all of its columns at once.
      if (specHidden) hiddenColIds.push(col.colId);
    }
  }

  return {
    rowIdPath: "nodeId",
    // On a phone the pinned sub-topic column eats most of the width.
    freezeFirstCol: !isMobile,
    // the border stays put while the columns scroll (mobile has no pinned column to hold the left edge)
    frameOutsideScroll: true,
    compactHeader: true,
    minColWidth: 60,
    colWidths,
    cellData,
    hiddenColIds,
  };
};

export const PrioritiesTableV2 = ({
  priorities,
  wins,
  meta,
  titleByContentType,
  onRowClick,
  // Which list opens, and whether the viewer gets the Priorities / Wins
  // switch at all (a report widget may pin one list).
  initialTab = "priorities",
  showTabs = true,
  // colIds to leave out — see COLUMN_SPECS / PRIORITIES_TABLE_COLUMN_OPTIONS.
  hiddenColumns = NO_COLUMNS_HIDDEN,
  // What the rows are: "findings" (sub-topics) or "buckets" (whole topics) —
  // the layout's grain. Names the first column and drops the parent-topic
  // column when there is no parent to show.
  scoreGrain = "findings",
}) => {
  const categoryTheme = useSignalCategories();
  const isBuckets = scoreGrain === "buckets";
  const [tab, setTab] = useState(initialTab);
  const isWins = tab === "wins";

  const maxSeverity = useMemo(
    () => [...priorities, ...wins].reduce((m, i) => Math.max(m, i.severity.value), 0),
    [priorities, wins]
  );
  const rows = useMemo(() => {
    const zones = resolveZones(categoryTheme.prioritiesCopy);
    const win = resolveWinCopy(categoryTheme.prioritiesCopy);
    return (isWins ? wins : priorities).map((item) => rowOf({ item, meta, isWins, zones, win, categoryTheme }));
  }, [priorities, wins, isWins, meta, categoryTheme]);
  const signalOrder = useMemo(() => signalOrderOf(rows, categoryTheme), [rows, categoryTheme]);
  const isMobile = useScreenWidth() < getBreakPoint("md");
  const tableConfig = useMemo(
    () => tableConfigOf({ meta, maxSeverity, titleByContentType, signalOrder, hiddenColumns, isMobile, isBuckets }),
    [meta, maxSeverity, titleByContentType, signalOrder, hiddenColumns, isMobile, isBuckets]
  );

  return (
    <div>
      {showTabs && (
        <div style={{ marginBottom: 8 }}>
          <PillSwitch
            options={[
              { value: "priorities", display: `Priorities (${priorities.length})` },
              { value: "wins", display: `Wins (${wins.length})` },
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>
      )}
      <TableElem
        id={`topicsV2:priorities:${tab}`}
        status="success"
        data={rows}
        tableConfig={tableConfig}
        handleRowClick={(row) => onRowClick?.(row.row)}
      />
    </div>
  );
};
